import type { ComputedRef, Ref } from "vue";
import { computed, onScopeDispose, ref, shallowRef, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { v4 } from "uuid";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { ConvertableUnit } from "@/libs/units";
import type { PumpTornadoChartViewData } from "../../d";
import type { PrecomputedPredictions } from "../usePredictiveCalcs";
import {
  getTornadoChartFrequencies,
  serializeScenario,
  type TornadoChartRequest
} from "./tornadoChartJobs";
import { decodeUnits } from "./unitCodec";
import type { PoolTask } from "./tornadoWorkerPool";
import { getChartPool } from "./tornadoPools";
import { calculationInputsJson, hashKey } from "./tornadoCacheKey";
import { acquirePrediction, type PredictionLease } from "./tornadoPredictions";
import { getPersistentStore, ResultCache } from "./tornadoResultCache";

// Tornado chart data computed off the main thread.
//
// Each frequency's flow-rate prediction is independent and by far the most
// expensive part, so they run in parallel in a pool of workers (and are cached
// one frequency at a time; see tornadoPredictions). Meanwhile a separate
// worker builds the chart context, which includes the hypothetical flow rate
// sweep, then finishes the chart once the predictions arrive. Inputs are
// debounced, superseded work is cancelled, and finished charts are cached in
// memory and IndexedDB. The results are identical to usePumpChartingData
// (see tornadoChartJobs.spec.ts).

// Charts are stored as posted (units encoded) so IndexedDB can hold them.
// A scenario without a chart is cached too, as { encoded: null }.
const chartCache = new ResultCache<{ encoded: unknown }>({
  name: "charts",
  memorySize: 20,
  persistentSize: 60,
  store: getPersistentStore()
});

const DEBOUNCE_MS = 400;

export type TornadoChartUnitRefs = {
  liquidFlow: Ref<ConvertableUnit> | ComputedRef<ConvertableUnit>;
  power: Ref<ConvertableUnit> | ComputedRef<ConvertableUnit>;
  largeLength: Ref<ConvertableUnit> | ComputedRef<ConvertableUnit>;
  pressure: Ref<ConvertableUnit> | ComputedRef<ConvertableUnit>;
};

export const useTornadoChartData = (options: {
  scenarioCalculations: ScenarioCalculations;
  minFrequency: Ref<number> | ComputedRef<number>;
  maxFrequency: Ref<number> | ComputedRef<number>;
  units: TornadoChartUnitRefs;
  displayRealHzLine?: boolean;
}) => {
  const data = shallowRef<PumpTornadoChartViewData | undefined>();
  const pending = ref(false);
  const error = ref<string | null>(null);

  // Serializing reads the whole scenario, so this recomputes on any input
  // change; the key then tells us whether the chart actually needs redoing.
  const request = computed<TornadoChartRequest>(() => ({
    scenario: serializeScenario(
      options.scenarioCalculations.destructuredScenario
    ),
    minFrequency: options.minFrequency.value,
    maxFrequency: options.maxFrequency.value,
    units: {
      liquidFlow: options.units.liquidFlow.value.label,
      power: options.units.power.value.label,
      largeLength: options.units.largeLength.value.label,
      pressure: options.units.pressure.value.label
    },
    displayRealHzLine: options.displayRealHzLine
  }));
  const inputsJson = computed(() =>
    calculationInputsJson(request.value.scenario)
  );
  const requestKey = computed(() =>
    JSON.stringify({ ...request.value, scenario: inputsJson.value })
  );

  // Each start() supersedes the previous one.
  let generation = 0;
  let activeKey: string | undefined;
  let leases: PredictionLease[] = [];
  let chartTask: PoolTask | undefined;
  const cancelActive = () => {
    generation++;
    activeKey = undefined;
    leases.forEach(lease => lease.release());
    leases = [];
    chartTask?.cancel();
    chartTask = undefined;
  };

  const show = (encoded: unknown) => {
    data.value = decodeUnits<PumpTornadoChartViewData | undefined>(encoded);
    error.value = null;
    pending.value = false;
  };

  const start = async () => {
    const key = requestKey.value;
    if (key === activeKey) return;
    cancelActive();
    const run = generation;
    activeKey = key;
    pending.value = true;
    const req = request.value;
    const isCurrent = () => run === generation;

    const [chartKey, scenarioHash] = await Promise.all([
      hashKey(key),
      hashKey(inputsJson.value)
    ]);
    if (!isCurrent()) return;
    const cached = await chartCache.get(chartKey);
    if (!isCurrent()) return;
    if (cached) {
      show(cached.encoded);
      activeKey = undefined;
      return;
    }

    const id = v4();
    const frequencies = getTornadoChartFrequencies(
      req.scenario,
      req.minFrequency,
      req.maxFrequency
    );
    leases = frequencies.map(frequency =>
      acquirePrediction(scenarioHash, req.scenario, frequency)
    );
    const task = getChartPool().run({ id, type: "chart", request: req });
    chartTask = task;

    // A failed prediction is left out; the chart worker then solves that
    // frequency itself, and reports the error if it fails there too.
    const settled = await Promise.allSettled(leases.map(l => l.promise));
    if (!isCurrent()) return;
    const predictions: PrecomputedPredictions = {};
    settled.forEach((result, i) => {
      if (result.status === "fulfilled") {
        predictions[frequencies[i]] = result.value;
      }
    });
    task.send({ id, type: "predictions", predictions });

    try {
      const encoded = await task.promise;
      if (!isCurrent()) return;
      chartCache.set(chartKey, { encoded });
      show(encoded);
    } catch (err) {
      if (!isCurrent()) return;
      error.value = err instanceof Error ? err.message : String(err);
      pending.value = false;
    }
    leases.forEach(lease => lease.release());
    leases = [];
    chartTask = undefined;
    activeKey = undefined;
  };
  const startDebounced = useDebounceFn(start, DEBOUNCE_MS);

  let started = false;
  watch(
    requestKey,
    () => {
      // Show something straight away the first time; debounce edits after.
      if (!started) {
        started = true;
        start();
      } else {
        pending.value = true;
        startDebounced();
      }
    },
    { immediate: true }
  );

  onScopeDispose(cancelActive);

  return { data, pending, error };
};
